import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  FORMULA_ERROR_STRINGS,
  type FormulaErrorStrings,
  formulaErrorMessage,
} from "@/components/tools/chemistry/formula-view";
import type {
  Substance,
  SubstanceMode,
} from "@/lib/tool-kit/chemistry/substance";

/** English strings for the "formula or molar mass" input. Widgets spread these in. */
export const SUBSTANCE_STRINGS = {
  substanceLabel: "The substance is given as",
  modeFormula: "A chemical formula",
  modeMass: "A molar mass",
  formulaLabel: "Chemical formula",
  formulaHint:
    "Case matters: Co is cobalt, CO is carbon monoxide. Hydrates use · or *, charges use ^ (Fe^3+).",
  molarMassLabel: "Molar mass (g/mol)",
  molarMassHint: "Use this for a substance whose formula you do not have.",
  enterSubstance: "Enter a chemical formula or a molar mass to begin.",
  invalidMolarMass: "Enter a molar mass greater than zero, in g/mol.",
  formulaErrors: FORMULA_ERROR_STRINGS,
} as const;

type SubstanceStrings = {
  [K in keyof typeof SUBSTANCE_STRINGS]: K extends "formulaErrors"
    ? FormulaErrorStrings
    : string;
};

/** Pill switch plus the field it reveals. `id` prefixes every control id. */
export function SubstanceFields({
  id,
  strings,
  mode,
  onModeChange,
  formula,
  onFormulaChange,
  molarMass,
  onMolarMassChange,
  formulaPlaceholder,
  molarMassPlaceholder,
}: {
  id: string;
  strings: SubstanceStrings;
  mode: SubstanceMode;
  onModeChange: (mode: SubstanceMode) => void;
  formula: string;
  onFormulaChange: (value: string) => void;
  molarMass: string;
  onMolarMassChange: (value: string) => void;
  formulaPlaceholder: string;
  molarMassPlaceholder: string;
}) {
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.substanceLabel}
        name={`${id}-substance`}
        onChange={onModeChange}
        options={[
          { value: "formula", label: strings.modeFormula },
          { value: "mass", label: strings.modeMass },
        ]}
        value={mode}
      />
      {mode === "formula" ? (
        <TextField
          hint={strings.formulaHint}
          id={`${id}-formula`}
          label={strings.formulaLabel}
          mono
          onChange={onFormulaChange}
          placeholder={formulaPlaceholder}
          value={formula}
        />
      ) : (
        <TextField
          hint={strings.molarMassHint}
          id={`${id}-molar-mass`}
          label={strings.molarMassLabel}
          numeric
          onChange={onMolarMassChange}
          placeholder={molarMassPlaceholder}
          value={molarMass}
        />
      )}
    </div>
  );
}

/** The message for a substance that is not usable yet, or null when it is. */
export function SubstanceProblem({
  substance,
  strings,
}: {
  substance: Substance;
  strings: SubstanceStrings;
}) {
  if (substance.kind === "ok") {
    return null;
  }
  if (substance.kind === "empty") {
    return <Notice>{strings.enterSubstance}</Notice>;
  }
  if (substance.kind === "invalidMass") {
    return <ErrorNotice>{strings.invalidMolarMass}</ErrorNotice>;
  }
  return (
    <ErrorNotice>
      {formulaErrorMessage(strings.formulaErrors, substance.error)}
    </ErrorNotice>
  );
}
